import React, { useEffect, useState, Component } from 'react';
import { Activity, Database, Terminal, Code, Layers, Search, Cpu, Server, Wrench, Zap, RefreshCw, Trash2, TrendingUp, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, AppState, GroupId } from '../../types';
import { useAppStore } from '../../hooks/useAppStore';
import { isStrictMode, setStrictMode } from '../../utils/strictMode';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { TaxonomyCoverage } from './coverage/TaxonomyCoverage';
import { SeedStatus } from './diagnostics/SeedStatus';
import { QualityWorkspace } from './quality/QualityWorkspace';
import { ValidationRunPanel } from './validation/ValidationRunPanel';
// Import existing panels
import { DevSystemPanel } from './overview/DevSystemPanel';
import { QuestionTypesPanel } from './overview/QuestionTypesPanel';
import { QuestionExplorer } from './data/QuestionExplorer';
import { FamilyExplorer } from './data/FamilyExplorer';
import { StateExplorer } from './data/StateExplorer';
interface DeveloperPageProps {
  questions: Question[];
  state: AppState;
  selectedGroup: GroupId;
  onNavigate?: (view: string) => void;
}
type TabId = 'system' | 'data' | 'coverage' | 'quality' | 'validation' | 'tools';
const TABS: {
  id: TabId;
  label: string;
  icon: any;
}[] = [{
  id: 'system',
  label: 'Systeem',
  icon: Activity
}, {
  id: 'data',
  label: 'Data',
  icon: Database
}, {
  id: 'coverage',
  label: 'Coverage',
  icon: Layers
}, {
  id: 'quality',
  label: 'Kwaliteit',
  icon: Zap
}, {
  id: 'validation',
  label: 'Validatie',
  icon: Cpu
}, {
  id: 'tools',
  label: 'Tools',
  icon: Wrench
}];
export function DeveloperPage({
  questions,
  state,
  selectedGroup,
  onNavigate
}: DeveloperPageProps) {
  const {
    devJumpToLevel,
    devResetProgress
  } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabId>('system');
  const [refreshKey, setRefreshKey] = useState(0);
  const [strictModeEnabled, setStrictModeEnabled] = useState(isStrictMode());
  const toggleStrictMode = () => {
    const newState = !strictModeEnabled;
    setStrictMode(newState);
    setStrictModeEnabled(newState);
    setRefreshKey(k => k + 1);
  };
  const handleRefresh = () => setRefreshKey(k => k + 1);
  return <div className="space-y-6" data-id="element-1756">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1757">
        <div className="flex items-center gap-3" data-id="element-1758">
          <div className="p-2.5 bg-indigo-100 text-indigo-600 rounded-xl" data-id="element-1759">
            <Terminal className="w-6 h-6" data-id="element-1760" />
          </div>
          <div data-id="element-1761">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight" data-id="element-1762">
              Developer Console
            </h1>
            <p className="text-sm text-slate-500 font-medium" data-id="element-1763">
              System Internals & Data Observatory
            </p>
          </div>
        </div>
        <Badge variant="neutral" className="self-start sm:self-auto" data-id="element-1764">
          RC 4.1
        </Badge>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1.5 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto scrollbar-hide" data-id="element-1765">
        {TABS.map(tab => {
        const isActive = activeTab === tab.id;
        const Icon = tab.icon;
        return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap
                ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
              `} data-id="element-1766">
              <Icon className="w-4 h-4" data-id="element-1767" />
              {tab.label}
            </button>;
      })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait" data-id="element-1768">
        <motion.div key={activeTab} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} data-id="element-1769">
          {/* ── TAB: SYSTEM ── */}
          {activeTab === 'system' && <div className="space-y-6" data-id="element-1770">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1771">
                {/* Main System Panel */}
                <div className="lg:col-span-2 space-y-6" data-id="element-1772">
                  <DevSystemPanel state={state} questions={questions} selectedGroup={selectedGroup} refreshKey={refreshKey} onRefresh={handleRefresh} onJumpToLevel={devJumpToLevel} onResetProgress={devResetProgress} onOpenGovernance={() => onNavigate?.('governance')} data-id="element-1773" />

                  <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1774">
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-1775">
                      <div className="flex items-center gap-2" data-id="element-1776">
                        <Code className="w-5 h-5 text-slate-500" data-id="element-1777" />
                        <h3 className="font-bold text-slate-700" data-id="element-1778">
                          Question Types Registry
                        </h3>
                      </div>
                      <Badge variant="neutral" data-id="element-1779">Config</Badge>
                    </div>
                    <div className="p-6" data-id="element-1780">
                      <QuestionTypesPanel data-id="element-1781" />
                    </div>
                  </Card>
                </div>

                {/* Sidebar Stats */}
                <div className="space-y-6" data-id="element-1782">
                  <SystemResourcesCard questionsCount={questions.length} stateSize={JSON.stringify(state).length} data-id="element-1783" />

                  <Card className="p-5" data-id="element-1784">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4" data-id="element-1785">
                      Environment
                    </h3>
                    <div className="space-y-3" data-id="element-1786">
                      <div className="flex justify-between items-center text-sm" data-id="element-1787">
                        <span className="text-slate-600" data-id="element-1788">Build</span>
                        <span className="font-mono font-bold text-slate-800" data-id="element-1789">
                          20260225
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm" data-id="element-1790">
                        <span className="text-slate-600" data-id="element-1791">React</span>
                        <span className="font-mono font-bold text-slate-800" data-id="element-1792">
                          {React.version}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm" data-id="element-1793">
                        <span className="text-slate-600" data-id="element-1794">Mode</span>
                        <Badge variant={process.env.NODE_ENV === 'development' ? 'warning' : 'success'} size="sm" data-id="element-1795">
                          {process.env.NODE_ENV}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>}

          {/* ── TAB: DATA ── */}
          {activeTab === 'data' && <div className="space-y-6" data-id="element-1796">
              <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1797">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2" data-id="element-1798">
                  <Search className="w-5 h-5 text-slate-400" data-id="element-1799" />
                  <h3 className="font-bold text-slate-700" data-id="element-1800">
                    Question Explorer
                  </h3>
                </div>
                <div className="h-[600px]" data-id="element-1801">
                  <QuestionExplorer questions={questions} data-id="element-1802" />
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1803">
                <Card className="overflow-hidden border-slate-200 shadow-sm flex flex-col h-[500px]" data-id="element-1804">
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 flex-shrink-0" data-id="element-1805">
                    <Layers className="w-5 h-5 text-slate-400" data-id="element-1806" />
                    <h3 className="font-bold text-slate-700" data-id="element-1807">
                      Family Explorer
                    </h3>
                  </div>
                  <div className="flex-1 overflow-hidden" data-id="element-1808">
                    <FamilyExplorer questions={questions} data-id="element-1809" />
                  </div>
                </Card>

                <Card className="overflow-hidden border-slate-200 shadow-sm flex flex-col h-[500px]" data-id="element-1810">
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 flex-shrink-0" data-id="element-1811">
                    <Server className="w-5 h-5 text-slate-400" data-id="element-1812" />
                    <h3 className="font-bold text-slate-700" data-id="element-1813">State Explorer</h3>
                  </div>
                  <div className="flex-1 overflow-auto" data-id="element-1814">
                    <StateExplorer state={state} selectedGroup={selectedGroup} data-id="element-1815" />
                  </div>
                </Card>
              </div>
            </div>}

          {/* ── TAB: COVERAGE ── */}
          {activeTab === 'coverage' && <div className="space-y-6" data-id="element-1816">
              <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1817">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-1818">
                  <div className="flex items-center gap-2" data-id="element-1819">
                    <Layers className="w-5 h-5 text-slate-500" data-id="element-1820" />
                    <h3 className="font-bold text-slate-700" data-id="element-1821">
                      Taxonomy Coverage
                    </h3>
                  </div>
                  <Badge variant="neutral" data-id="element-1822">
                    {questions.length} vragen geladen
                  </Badge>
                </div>
                <div className="p-6" data-id="element-1823">
                  <TaxonomyCoverage questions={questions} data-id="element-1824" />
                </div>
              </Card>

              <Card className="overflow-hidden border-slate-200 shadow-sm" data-id="element-1825">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2" data-id="element-1826">
                  <Database className="w-5 h-5 text-slate-400" data-id="element-1827" />
                  <h3 className="font-bold text-slate-700" data-id="element-1828">Seed Status</h3>
                </div>
                <div className="p-6" data-id="element-1829">
                  <SeedStatus data-id="element-1830" />
                </div>
              </Card>
            </div>}

          {/* ── TAB: QUALITY ── */}
          {activeTab === 'quality' && <div data-id="element-1831">
              <QualityWorkspace refreshKey={refreshKey} data-id="element-1832" />
            </div>}

          {/* ── TAB: VALIDATION ── */}
          {activeTab === 'validation' && <div data-id="element-1833">
              <ValidationRunPanel questions={questions} data-id="element-1834" />
            </div>}

          {/* ── TAB: TOOLS ── */}
          {activeTab === 'tools' && <div className="max-w-3xl space-y-6" data-id="element-1835">
              <Card className="p-6" data-id="element-1836">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1837">
                  <Wrench className="w-5 h-5 text-indigo-600" data-id="element-1838" />
                  Developer Controls
                </h3>

                <div className="space-y-6" data-id="element-1839">
                  {/* Strict Mode Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1840">
                    <div data-id="element-1841">
                      <h4 className="font-bold text-slate-900" data-id="element-1842">Strict Mode</h4>
                      <p className="text-sm text-slate-500" data-id="element-1843">
                        Bypass error boundaries to expose crashes
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-1844">
                      <input type="checkbox" checked={strictModeEnabled} onChange={toggleStrictMode} className="sr-only peer" data-id="element-1845" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600" data-id="element-1846"></div>
                    </label>
                  </div>

                  {/* Level Controls */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1847">
                    <h4 className="font-bold text-slate-900 mb-3" data-id="element-1848">
                      Level Simulation
                    </h4>
                    <div className="flex gap-3" data-id="element-1849">
                      <Button onClick={() => devJumpToLevel(1)} variant="secondary" size="sm" data-id="element-1850">
                        <RefreshCw className="w-4 h-4 mr-2" data-id="element-1851" />
                        Level 1
                      </Button>
                      <Button onClick={() => devJumpToLevel(5)} variant="secondary" size="sm" data-id="element-1852">
                        <TrendingUp className="w-4 h-4 mr-2" data-id="element-1853" />
                        Level 5
                      </Button>
                      <Button onClick={() => devJumpToLevel(10)} variant="secondary" size="sm" data-id="element-1854">
                        <TrendingUp className="w-4 h-4 mr-2" data-id="element-1855" />
                        Level 10
                      </Button>
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-1856">
                    <h4 className="font-bold text-slate-900 mb-3" data-id="element-1857">
                      Quick Navigation
                    </h4>
                    <Button onClick={() => onNavigate?.('governance')} variant="outline" size="sm" data-id="element-1858">
                      Open Governance Console
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1859" />
                    </Button>
                  </div>

                  {/* Danger Zone */}
                  <div className="p-4 bg-rose-50 rounded-xl border border-rose-100" data-id="element-1860">
                    <h4 className="font-bold text-rose-900 mb-3" data-id="element-1861">
                      Danger Zone
                    </h4>
                    <Button onClick={devResetProgress} variant="danger" size="sm" className="w-full sm:w-auto" data-id="element-1862">
                      <Trash2 className="w-4 h-4 mr-2" data-id="element-1863" />
                      Reset All Progress
                    </Button>
                  </div>
                </div>
              </Card>
            </div>}
        </motion.div>
      </AnimatePresence>
    </div>;
}
// --- Helper Components ---
function SystemResourcesCard({
  questionsCount,
  stateSize
}: {
  questionsCount: number;
  stateSize: number;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <Card className="p-6 bg-slate-900 text-white border-0 relative overflow-hidden shadow-xl shadow-slate-900/10" data-id="element-1864">
      {/* Gradient Mesh Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-emerald-500/10 pointer-events-none" data-id="element-1865" />
      <div className="absolute -right-10 -top-10 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" data-id="element-1866" />

      <div className="relative z-10" data-id="element-1867">
        <div className="flex items-center gap-3 mb-6" data-id="element-1868">
          <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm" data-id="element-1869">
            <Cpu className="w-5 h-5 text-indigo-300" data-id="element-1870" />
          </div>
          <h3 className="text-lg font-bold" data-id="element-1871">System Resources</h3>
        </div>

        <div className="space-y-6" data-id="element-1872">
          <div data-id="element-1873">
            <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider" data-id="element-1874">
              <span data-id="element-1875">Questions Loaded</span>
              <span className="text-white" data-id="element-1876">{questionsCount}</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1877">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-1000 ease-out" style={{
              width: mounted ? '100%' : '0%'
            }} data-id="element-1878" />
            </div>
          </div>

          <div data-id="element-1879">
            <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider" data-id="element-1880">
              <span data-id="element-1881">State Size</span>
              <span className="text-white" data-id="element-1882">
                {(stateSize / 1024).toFixed(1)} KB
              </span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1883">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-1000 ease-out delay-150" style={{
              width: mounted ? '25%' : '0%'
            }} data-id="element-1884" />
            </div>
          </div>

          <div className="pt-4 mt-2 border-t border-white/10 flex items-center justify-between text-xs text-slate-400" data-id="element-1885">
            <div className="flex items-center gap-2" data-id="element-1886">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1887" />
              <span data-id="element-1888">System Operational</span>
            </div>
            <span className="font-mono opacity-50" data-id="element-1889">MEM_OK</span>
          </div>
        </div>
      </div>
    </Card>;
}